import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import {setWindowTitle} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  TemplateCollectionPageSummaryQuery,
  TemplateCollectionPageSummaryQueryVariables,
} from 'notebook/entry-templates/collections/__generated/TemplateCollectionPageSummaryQuery';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import TemplateCollectionTable from 'notebook/entry-templates/collections/TemplateCollectionTable';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import SettingsPage from 'ui-library/SettingsPage';
import {capitalize, typeToName} from 'util/NameUtil';

export const TYPE_TO_LABEL = new Map<string, () => string>([
  [ModelTypeConstants.TEMPLATE, () => capitalize(typeToName(ModelTypeConstants.TEMPLATE))],
  [ModelTypeConstants.SUBTEMPLATE, () => capitalize(typeToName(ModelTypeConstants.SUBTEMPLATE))],
  [ModelTypeConstants.ANALYSIS_TEMPLATE, () => capitalize(typeToName(ModelTypeConstants.ANALYSIS_TEMPLATE))],
  [ModelTypeConstants.FORM_DEFINITION, () => capitalize(typeToName(ModelTypeConstants.FORM_DEFINITION))],
  [ModelTypeConstants.RECIPE_PROCEDURE, () => capitalize(typeToName(ModelTypeConstants.RECIPE_PROCEDURE))],
  [ModelTypeConstants.ASSAY_PROCEDURE, () => capitalize(typeToName(ModelTypeConstants.ASSAY_PROCEDURE))],
  // Use procedure if you don't need to distinguish between recipes and assays
  [ModelTypeConstants.PROCEDURE, () => typeToName(ModelTypeConstants.PROCEDURE)],
  [ModelTypeConstants.REQUEST_V2_DEFINITION, () => typeToName(ModelTypeConstants.REQUEST_V2_DEFINITION)],
]);

export const TEMPLATE_COLLECTION_PAGE_SUMMARY_QUERY = gql`
  query TemplateCollectionPageSummaryQuery($collectionId: StrictString!) @skipAudit {
    templateCollection(id: $collectionId) {
      ... on TemplateCollectionNode {
        id
        name
        archiveRecord {
          purpose
        }
        description
        ownerJson
        ownerInfoJson
        collaboratorsJson
        url
        counts
        createdAt
        modifiedAt
        permissions {
          ...TemplateCollectionPermissionsGraphQL
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('TemplateCollectionPermissions').fragment}
`;

interface Props {
  collectionId: string;
}

export default function TemplateCollectionPage({collectionId}: Props): JSX.Element | null {
  return (
    <div className="u-flex-display u-vertical-flex u-height-full">
      <div className="u-flex-full-height">
        <BenchlingQuery<TemplateCollectionPageSummaryQuery, TemplateCollectionPageSummaryQueryVariables>
          query={TEMPLATE_COLLECTION_PAGE_SUMMARY_QUERY}
          variables={{collectionId}}
          fetchPolicy="cache-and-network"
          renderLoading={() => (
            <SettingsPage
              breadcrumbs={[{name: 'Template Collections', url: TemplateCollectionDAO.collectionsURL()}]}
            >
              <CenteredHorizontalThrobber />
            </SettingsPage>
          )}
        >
          {(data, result) => {
            assert(!isInaccessible(data.templateCollection), 'Expected result schema to be accessible.');
            const collection = data.templateCollection;
            setWindowTitle(`${collection.name} - Template Collections`);
            return (
              <SettingsPage
                breadcrumbs={[{name: 'Template Collections', url: TemplateCollectionDAO.collectionsURL()}]}
                title={
                  collection != null
                    ? collection.archiveRecord
                      ? `${collection.name} (${collection.archiveRecord.purpose})`
                      : collection.name
                    : undefined
                }
                type={typeToName(ModelTypeConstants.TEMPLATE_COLLECTION)}
                dataTestAttrs={{component: 'TemplateCollectionPage', element: 'root'}}
              >
                <TemplateCollectionTable
                  collection={collection}
                  resultType={result.type}
                  summaryRefetch={result.refetch}
                />
              </SettingsPage>
            );
          }}
        </BenchlingQuery>
      </div>
    </div>
  );
}
